import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import VoucherCard from '../gift-cards-v2/VoucherCard';
import { DESIGNS, getDesign } from '../gift-cards-v2/data';
import { buildPreviewVoucher } from './shopUtils';
import '../gift-cards-v2/GiftCardsV2.css';
import './GiftCardEmailPreview.css';

const Spark = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 2l2.1 6.3a3 3 0 0 0 1.6 1.6L22 12l-6.3 2.1a3 3 0 0 0-1.6 1.6L12 22l-2.1-6.3a3 3 0 0 0-1.6-1.6L2 12l6.3-2.1a3 3 0 0 0 1.6-1.6L12 2z" />
  </svg>
);

// Two scenarios to review. Real emails are rendered/sent by the backend; this is
// a front-end preview/spec of how the recipient's email looks.
const SAMPLES = {
  gift: {
    salonName: 'Rouge Parlour',
    designId: 'editorial-birthday',
    amount: 50,
    code: 'BEU-7F2A-9KQ3-8M1X',
    recipient: 'Alex',
    from: 'Jordan',
    message: 'Happy birthday! Treat yourself to something lovely.',
    expiry: '18 Dec 2026',
    undesigned: false,
  },
  salon: {
    salonName: 'The Glasshouse',
    designId: null,
    amount: 30,
    code: 'BEU-GIFT-2654',
    recipient: '',
    from: '',
    message: '',
    expiry: '12 May 2027',
    undesigned: true,
  },
};

// GiftCardEmailPreview — reviewable mock of the gift-card email. The "Add to your
// wallet" button points at the real claim route (/gift-cards/claim/:code).
const GiftCardEmailPreview = () => {
  const history = useHistory();
  const [caseKey, setCaseKey] = useState('gift');
  const s = SAMPLES[caseKey];
  const design = s.undesigned ? null : getDesign(DESIGNS, s.designId);
  const voucher = buildPreviewVoucher(design, s.amount);

  return (
    <div className="gce-page">
      <div className="gce-bar">
        <span className="gce-bar-label">Email preview</span>
        <div className="gce-toggle">
          <button type="button" className={caseKey === 'gift' ? 'is-active' : ''} onClick={() => setCaseKey('gift')}>
            Gifted (with design)
          </button>
          <button type="button" className={caseKey === 'salon' ? 'is-active' : ''} onClick={() => setCaseKey('salon')}>
            In salon (no design)
          </button>
        </div>
      </div>

      <div className="gce-email">
        <div className="gce-brand">beu</div>
        <div className="gce-card">
          <h1 className="gce-headline">
            {s.undesigned ? 'Here’s your gift card' : `${s.from} sent you a gift card`}
          </h1>
          {s.recipient && <p className="gce-greet">Hi {s.recipient},</p>}
          {s.message && <p className="gce-message">“{s.message}”</p>}

          {s.undesigned ? (
            <div className="gce-art gce-art--undesigned">
              <span className="gce-spark"><Spark /></span>
              <span className="gce-undesigned-title">Choose your design</span>
              <span className="gce-undesigned-sub">Make it yours in the app</span>
            </div>
          ) : (
            <div className="voucher-v2 gce-art">
              <VoucherCard
                voucher={voucher}
                aesthetic={design ? design.aesthetic : 'custom'}
                salonName={s.salonName}
                currency="£"
                showPromo={false}
              />
            </div>
          )}

          <div className="gce-amount">{`£${s.amount} to spend at ${s.salonName}`}</div>

          <div className="gce-code-block">
            <span className="gce-code-label">Gift card code</span>
            <span className="gce-code">{s.code}</span>
          </div>

          <button type="button" className="gce-cta" onClick={() => history.push(`/gift-cards/claim/${s.code}`)}>
            {s.undesigned ? 'Add to wallet and choose a design' : 'Add to your wallet'}
          </button>
          <p className="gce-hint">Or use the code at {s.salonName} in person.</p>

          <p className="gce-expiry">Valid until {s.expiry}</p>
        </div>
        <p className="gce-footer">Sent by {s.salonName} via Beu. Terms apply.</p>
      </div>
    </div>
  );
};

export default GiftCardEmailPreview;
